import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getImportReport, GetImportReportOutputType } from 'zite-endpoints-sdk';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { FileText, BookOpen, Shield, AlertTriangle, CheckCircle, XCircle, Search, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

type Report = GetImportReportOutputType;

function StatCard({ icon: Icon, label, total, draft, color }: { icon: typeof FileText; label: string; total: number; draft: number; color: string }) {
  return (
    <div className="border border-border rounded-lg bg-card p-4">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${color}`} />
        <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-foreground">{total}</p>
      <p className="text-xs text-muted-foreground mt-0.5">{draft} draft · {total - draft} other</p>
    </div>
  );
}

function DryRunPanel({ data, onClose }: { data: Report; onClose: () => void }) {
  // Compute integrity checks from current data
  const sopsMissingCategory = data.sops.filter(s => !s.category);
  const sopsMissingSource = data.sops.filter(s => !s.sourceProvenance);
  const sopsNoContent = data.sops.filter(s => !s.hasContent);
  const sopsWithConflicts = data.sops.filter(s => !!s.conflictNotes);
  const sopsNotDraft = data.sops.filter(s => s.sopStatus !== 'Draft');

  const policiesMissingSource = data.policies.filter(p => !p.sourceProvenance);
  const policiesNoContent = data.policies.filter(p => !p.hasBody);
  const policiesNotDraft = data.policies.filter(p => p.lifecycleStatus !== 'Draft');

  const handbookNoContent = data.handbook.filter(h => !h.hasBody);
  const handbookNotDraft = data.handbook.filter(h => h.status !== 'Draft');

  // Detect title duplicates within SOPs
  const sopTitleCounts: Record<string, number> = {};
  for (const s of data.sops) {
    const key = s.title.toLowerCase().trim();
    sopTitleCounts[key] = (sopTitleCounts[key] || 0) + 1;
  }
  const duplicateSopTitles = Object.entries(sopTitleCounts).filter(([, c]) => c > 1);

  type CheckItem = { label: string; status: 'pass' | 'fail' | 'warn'; detail: string };
  const checks: CheckItem[] = [
    { label: 'SOP count', status: 'pass', detail: `${data.summary.totalSops} SOPs in database` },
    { label: 'SOP duplicates (by title)', status: duplicateSopTitles.length > 0 ? 'fail' : 'pass', detail: duplicateSopTitles.length > 0 ? `${duplicateSopTitles.length} duplicate title groups found: ${duplicateSopTitles.map(([t, c]) => `"${t}" ×${c}`).join(', ')}` : 'No duplicate titles' },
    { label: 'SOP categories populated', status: sopsMissingCategory.length > 0 ? 'warn' : 'pass', detail: sopsMissingCategory.length > 0 ? `${sopsMissingCategory.length} SOPs missing category` : 'All SOPs have categories' },
    { label: 'SOP source provenance', status: sopsMissingSource.length > 0 ? 'warn' : 'pass', detail: sopsMissingSource.length > 0 ? `${sopsMissingSource.length} SOPs missing source provenance` : 'All SOPs have source provenance' },
    { label: 'SOP content', status: sopsNoContent.length > 0 ? 'fail' : 'pass', detail: sopsNoContent.length > 0 ? `${sopsNoContent.length} SOPs with no/empty content` : 'All SOPs have content' },
    { label: 'SOP status = Draft', status: sopsNotDraft.length > 0 ? 'warn' : 'pass', detail: sopsNotDraft.length > 0 ? `${sopsNotDraft.length} SOPs not in Draft status` : 'All SOPs are Draft' },
    { label: 'SOP conflicts', status: sopsWithConflicts.length > 0 ? 'warn' : 'pass', detail: sopsWithConflicts.length > 0 ? `${sopsWithConflicts.length} SOPs have conflict notes` : 'No conflicts' },
    { label: 'Policy count', status: data.summary.totalPolicies === 0 ? 'warn' : 'pass', detail: `${data.summary.totalPolicies} policies in database` },
    { label: 'Policy content', status: policiesNoContent.length > 0 ? 'fail' : 'pass', detail: policiesNoContent.length > 0 ? `${policiesNoContent.length} policies with no content` : data.summary.totalPolicies === 0 ? 'No policies to check' : 'All policies have content' },
    { label: 'Policy status = Draft', status: policiesNotDraft.length > 0 ? 'warn' : 'pass', detail: policiesNotDraft.length > 0 ? `${policiesNotDraft.length} policies not in Draft status` : data.summary.totalPolicies === 0 ? 'No policies to check' : 'All policies are Draft' },
    { label: 'Policy source provenance', status: policiesMissingSource.length > 0 ? 'warn' : 'pass', detail: policiesMissingSource.length > 0 ? `${policiesMissingSource.length} policies missing source` : data.summary.totalPolicies === 0 ? 'No policies to check' : 'All policies have provenance' },
    { label: 'Handbook count', status: 'pass', detail: `${data.summary.totalHandbook} handbook sections in database` },
    { label: 'Handbook content', status: handbookNoContent.length > 0 ? 'fail' : 'pass', detail: handbookNoContent.length > 0 ? `${handbookNoContent.length} sections with no content` : 'All sections have content' },
    { label: 'Handbook status = Draft', status: handbookNotDraft.length > 0 ? 'warn' : 'pass', detail: handbookNotDraft.length > 0 ? `${handbookNotDraft.length} sections not in Draft status` : 'All sections are Draft' },
  ];

  const passes = checks.filter(c => c.status === 'pass').length;
  const fails = checks.filter(c => c.status === 'fail').length;
  const warns = checks.filter(c => c.status === 'warn').length;

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Dry Run — Data Integrity Scan</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-green-500/10 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-green-700">{passes}</p>
              <p className="text-xs text-muted-foreground">Pass</p>
            </div>
            <div className="bg-yellow-500/10 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-yellow-700">{warns}</p>
              <p className="text-xs text-muted-foreground">Warnings</p>
            </div>
            <div className="bg-destructive/10 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-destructive">{fails}</p>
              <p className="text-xs text-muted-foreground">Failures</p>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs">
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="font-bold text-foreground">{data.summary.totalSops}</p>
              <p className="text-muted-foreground">SOPs</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="font-bold text-foreground">{data.summary.draftSops}</p>
              <p className="text-muted-foreground">Draft SOPs</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="font-bold text-foreground">{data.summary.totalPolicies}</p>
              <p className="text-muted-foreground">Policies</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="font-bold text-foreground">{data.summary.totalHandbook}</p>
              <p className="text-muted-foreground">Handbook</p>
            </div>
            <div className="bg-muted/50 rounded-lg p-2 text-center">
              <p className="font-bold text-foreground">{data.summary.conflictCount}</p>
              <p className="text-muted-foreground">Conflicts</p>
            </div>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Check</th>
                  <th className="text-center px-3 py-2 text-xs font-medium text-muted-foreground w-16">Result</th>
                  <th className="text-left px-3 py-2 text-xs font-medium text-muted-foreground">Detail</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((c, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-3 py-2 font-medium text-foreground">{c.label}</td>
                    <td className="px-3 py-2 text-center">
                      {c.status === 'pass' ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> :
                       c.status === 'fail' ? <XCircle className="w-4 h-4 text-destructive mx-auto" /> :
                       <AlertTriangle className="w-4 h-4 text-yellow-500 mx-auto" />}
                    </td>
                    <td className="px-3 py-2 text-xs text-muted-foreground">{c.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function ImportReport() {
  const { user } = useAuth();
  const [data, setData] = useState<Report | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDryRun, setShowDryRun] = useState(false);

  const isAdmin = ['Admin', 'Owner', 'Manager'].includes(user?.role || '');

  const load = () => {
    if (!user) return;
    setLoading(true);
    getImportReport({})
      .then(setData)
      .catch(() => toast.error('Failed to load import report'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [user]);

  if (loading) return (
    <div className="px-6 py-8 max-w-5xl mx-auto space-y-4">
      <Skeleton className="h-8 w-64" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-24" />)}</div>
      <Skeleton className="h-64" />
    </div>
  );

  if (!data) return <div className="text-center py-16 text-muted-foreground">No data available</div>;

  const q = search.toLowerCase();

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-card border-b border-border px-6 md:px-8 py-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-foreground">Import Report</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Overview of all imported content — SOPs, policies, and handbook sections</p>
          </div>
          {isAdmin && (
            <Button variant="outline" size="sm" onClick={() => setShowDryRun(true)} className="gap-1.5">
              <Eye className="w-3.5 h-3.5" />
              Dry Run Preview
            </Button>
          )}
        </div>
      </div>

      <div className="px-6 md:px-8 py-6 max-w-6xl mx-auto space-y-6">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard icon={FileText} label="SOPs" total={data.summary.totalSops} draft={data.summary.draftSops} color="text-primary" />
          <StatCard icon={Shield} label="Policies" total={data.summary.totalPolicies} draft={data.summary.draftPolicies} color="text-blue-500" />
          <StatCard icon={BookOpen} label="Handbook" total={data.summary.totalHandbook} draft={data.summary.draftHandbook} color="text-green-500" />
          <div className="border border-border rounded-lg bg-card p-4">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-4 h-4 text-destructive" />
              <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">Conflicts</span>
            </div>
            <p className="text-2xl font-bold text-foreground">{data.summary.conflictCount}</p>
            <p className="text-xs text-muted-foreground mt-0.5">SOPs with conflict notes</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
          <Input placeholder="Filter by title..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-9 text-sm" />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="sops">
          <TabsList>
            <TabsTrigger value="sops">SOPs ({data.sops.length})</TabsTrigger>
            <TabsTrigger value="policies">Policies ({data.policies.length})</TabsTrigger>
            <TabsTrigger value="handbook">Handbook ({data.handbook.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="sops" className="mt-4">
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Title</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Category</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Source</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Content</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Conflicts</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sops.filter(s => !q || s.title.toLowerCase().includes(q)).map(s => (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-foreground font-medium">{s.title}</td>
                      <td className="px-4 py-2.5 text-muted-foreground">{s.category || '—'}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={s.sopStatus === 'Draft' ? 'text-yellow-700 bg-yellow-500/10' : 'text-green-700 bg-green-500/10'}>{s.sopStatus}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[200px] truncate">{s.sourceProvenance || '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {s.hasContent ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground mx-auto" />}
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {s.conflictNotes ? <AlertTriangle className="w-4 h-4 text-destructive mx-auto" /> : <span className="text-muted-foreground">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.sops.filter(s => !q || s.title.toLowerCase().includes(q)).length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-8">No SOPs found</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="policies" className="mt-4">
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Policy Name</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Version</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Source</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Content</th>
                  </tr>
                </thead>
                <tbody>
                  {data.policies.filter(p => !q || p.policyName.toLowerCase().includes(q)).map(p => (
                    <tr key={p.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-foreground font-medium">{p.policyName}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={p.lifecycleStatus === 'Draft' ? 'text-yellow-700 bg-yellow-500/10' : 'text-green-700 bg-green-500/10'}>{p.lifecycleStatus}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-muted-foreground">{p.version}</td>
                      <td className="px-4 py-2.5 text-xs text-muted-foreground max-w-[200px] truncate">{p.sourceProvenance || '—'}</td>
                      <td className="px-4 py-2.5 text-center">
                        {p.hasBody ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground mx-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.policies.filter(p => !q || p.policyName.toLowerCase().includes(q)).length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-8">No policies found</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="handbook" className="mt-4">
            <div className="border border-border rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">#</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Section Title</th>
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                    <th className="text-center px-4 py-2.5 text-xs font-medium text-muted-foreground">Content</th>
                  </tr>
                </thead>
                <tbody>
                  {data.handbook.filter(h => !q || h.title.toLowerCase().includes(q)).map(h => (
                    <tr key={h.id} className="border-t border-border hover:bg-muted/30">
                      <td className="px-4 py-2.5 text-muted-foreground">{h.sectionOrder}</td>
                      <td className="px-4 py-2.5 text-foreground font-medium">{h.title}</td>
                      <td className="px-4 py-2.5">
                        <Badge variant="outline" className={h.status === 'Draft' ? 'text-yellow-700 bg-yellow-500/10' : 'text-green-700 bg-green-500/10'}>{h.status}</Badge>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {h.hasBody ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" /> : <XCircle className="w-4 h-4 text-muted-foreground mx-auto" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data.handbook.filter(h => !q || h.title.toLowerCase().includes(q)).length === 0 && (
                <p className="text-center text-muted-foreground text-sm py-8">No handbook sections found</p>
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Dry Run modal */}
      {showDryRun && data && (
        <DryRunPanel data={data} onClose={() => setShowDryRun(false)} />
      )}
    </div>
  );
}
